import React, { useEffect } from 'react'
import SafeLink from '../../components/common/SafeLink'
import {
  ArrowRight, Award, BookOpen, Brain, ClipboardCheck, FlaskConical,
  GraduationCap, Layers, Mail, MapPin, Microscope, Phone, Sparkles, Users,
} from 'lucide-react'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import { motion } from 'framer-motion'

// Trang Giới thiệu — nội dung chuẩn SEO cho AI-PhysicsBook (ebook.vatli365.vn)
const SEO_TITLE = 'Giới thiệu AI-PhysicsBook — Nền tảng dạy & học Vật lí tương tác với AI | vatli365'
const SEO_DESC =
  'AI-PhysicsBook (ebook.vatli365.vn) — nền tảng dạy và học Vật lí THPT ứng dụng AI: soạn bài giảng tương tác theo chuẩn sư phạm, tạo khoá học trực tuyến, thi online, mô phỏng thí nghiệm. Giải Nhất Giải thưởng tiên phong ứng dụng AI trong giáo dục Việt Nam 2025.'

const FEATURES = [
  { icon: Sparkles, title: 'Soạn bài giảng bằng AI', desc: 'Tải file Word, AI dựng bài giảng theo cấu trúc chuẩn sư phạm 4 hoạt động (Khởi động → Hình thành kiến thức → Luyện tập → Vận dụng), bám yêu cầu cần đạt của Chương trình GDPT 2018.' },
  { icon: BookOpen, title: 'Khoá học trực tuyến hoàn chỉnh', desc: 'Ghép bài giảng thành khoá học theo phân phối chương trình; học sinh ghi danh miễn phí, giáo viên theo dõi tiến độ và thống kê tương tác chi tiết từng phần.' },
  { icon: ClipboardCheck, title: 'Thi online & ngân hàng câu hỏi', desc: 'Đề thi trắc nghiệm chuẩn cấu trúc 2025-2026 (trắc nghiệm, đúng/sai, trả lời ngắn), chấm điểm tự động, sinh biến thể đề bằng AI.' },
  { icon: Microscope, title: 'Mô phỏng Vật lí tương tác', desc: 'Hơn 30 mô phỏng canvas và GeoGebra theo từng bài học lớp 10, 11, 12 — quan sát hiện tượng trực quan ngay trong bài giảng.' },
  { icon: FlaskConical, title: 'Thí nghiệm Lab IoT thật', desc: 'Kết nối thí nghiệm thật qua thiết bị ESP32 (lab.vatli365.vn): dao động, âm thanh, nhiệt học — dữ liệu truyền trực tiếp lên trình duyệt.' },
  { icon: Layers, title: 'Học liệu bổ trợ', desc: 'Flashcard công thức, Tiếng Anh chuyên ngành Vật lí (eng.vatli365.vn), AI gia sư hỏi đáp kiểu gợi mở giúp học sinh tự tìm ra đáp án.' },
]

const AUDIENCE = [
  { icon: Users, title: 'Giáo viên Vật lí', desc: 'Soạn bài giảng nhanh bằng AI, quản lý lớp, giao bài, chấm điểm tự động và nắm rõ học sinh đang vướng ở phần kiến thức nào.' },
  { icon: GraduationCap, title: 'Học sinh THPT', desc: 'Học theo phương pháp VISE — trực quan, chia nhỏ từng bước: trả lời đúng câu hỏi mới mở nội dung tiếp theo, hiểu bản chất vật lí thay vì học vẹt công thức.' },
  { icon: Brain, title: 'Nhà trường & tổ chuyên môn', desc: 'Chia sẻ kho bài giảng, đề thi dùng chung trong tổ; thống kê kết quả học tập toàn khối phục vụ sinh hoạt chuyên môn.' },
]

const GioiThieu: React.FC = () => {
  // SEO cho SPA: đặt title + meta description khi vào trang
  useEffect(() => {
    const prevTitle = document.title
    document.title = SEO_TITLE
    const meta = document.querySelector('meta[name="description"]')
    const prevDesc = meta?.getAttribute('content') || ''
    meta?.setAttribute('content', SEO_DESC)
    return () => {
      document.title = prevTitle
      meta?.setAttribute('content', prevDesc)
    }
  }, [])

  return (
    <div className="min-h-screen bg-transparent">
      <Navigation />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-14">
        {/* Tiêu đề */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 text-sm font-bold rounded-full mb-6 border border-amber-200">
            <Award className="w-4 h-4 mr-2 text-amber-500" />
            Giải Nhất — Giải thưởng tiên phong ứng dụng AI trong giáo dục Việt Nam 2025
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-5 leading-tight">
            AI-PhysicsBook — Nền tảng dạy &amp; học Vật lí tương tác với AI
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            AI-PhysicsBook (ebook.vatli365.vn) giúp giáo viên Vật lí <strong>soạn bài giảng tương tác bằng AI</strong>,
            dựng <strong>khoá học trực tuyến</strong> hoàn chỉnh và tổ chức <strong>thi online</strong> —
            còn học sinh được học qua mô phỏng, thí nghiệm và câu hỏi tương tác từng bước,
            đúng tinh thần đề thi tốt nghiệp THPT từ 2025.
          </p>
        </motion.header>

        {/* Câu chuyện */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          className="bg-white/80 backdrop-blur rounded-3xl border border-slate-100 shadow-sm p-8 md:p-10"
        >
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Vì sao có AI-PhysicsBook?</h2>
          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p>
              Nền tảng được xây dựng bởi <strong>thầy Trần Quốc Quân</strong> — giáo viên Vật lí
              Trường THPT Tây Sơn (Gia Lai) — từ chính nhu cầu trong lớp học: làm sao để học sinh
              <em> hiểu bản chất hiện tượng vật lí</em> thay vì chỉ nhớ công thức, và làm sao để
              giáo viên soạn được bài giảng tương tác chất lượng mà không mất hàng giờ mỗi bài.
            </p>
            <p>
              Năm 2025, sản phẩm đạt <strong>Giải Nhất Giải thưởng tiên phong ứng dụng AI trong
              giáo dục Việt Nam</strong>. Phiên bản hiện tại là bản nâng cấp đặc biệt từ sản phẩm
              đạt giải: bổ sung trợ lý AI soạn bài theo chuẩn sư phạm, hệ thống khoá học có ghi danh
              — thống kê, và bộ công cụ ra đề bám sát cấu trúc đề thi tốt nghiệp THPT 2025-2026.
            </p>
          </div>
        </motion.section>

        {/* Tính năng */}
        <section>
          <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center">Nền tảng có gì?</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  className="flex gap-4 p-5 bg-white/80 rounded-2xl border border-slate-100 shadow-sm"
                >
                  <span className="shrink-0 w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </span>
                  <span>
                    <h3 className="font-bold text-slate-800 mb-1">{f.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                  </span>
                </motion.div>
              )
            })}
          </div>
        </section>

        {/* Đối tượng */}
        <section>
          <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center">Dành cho ai?</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {AUDIENCE.map((a, i) => {
              const Icon = a.icon
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  className="p-6 bg-white/80 rounded-2xl border border-slate-100 shadow-sm text-center"
                >
                  <span className="inline-flex w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 items-center justify-center mb-3">
                    <Icon className="w-6 h-6" />
                  </span>
                  <h3 className="font-bold text-slate-800 mb-1.5">{a.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{a.desc}</p>
                </motion.div>
              )
            })}
          </div>
        </section>

        {/* Liên hệ + CTA */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-3xl p-8 md:p-10 text-white"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl font-bold mb-4">Liên hệ</h2>
              <ul className="space-y-2.5 text-slate-200 text-sm">
                <li className="flex items-center gap-2.5"><Users className="w-4 h-4 text-slate-400" /> Thầy Trần Quốc Quân — GV Vật lí, THPT Tây Sơn</li>
                <li className="flex items-center gap-2.5"><MapPin className="w-4 h-4 text-slate-400" /> Bình An, Gia Lai</li>
                <li className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-slate-400" /> <a href="tel:0978531191" className="hover:text-white">0978 531 191</a></li>
                <li className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-slate-400" /> <a href="mailto:quants82@gmail.com" className="hover:text-white">quants82@gmail.com</a></li>
              </ul>
            </div>
            <div className="text-center md:text-right">
              <p className="text-slate-300 mb-4">Sẵn sàng trải nghiệm?</p>
              <SafeLink
                to="/khoa-hoc"
                className="inline-flex items-center px-7 py-3.5 bg-white text-slate-800 font-bold rounded-xl shadow hover:bg-slate-100 transition-colors no-underline"
              >
                Khám phá khoá học <ArrowRight className="ml-2 w-5 h-5" />
              </SafeLink>
            </div>
          </div>
        </motion.section>
      </div>

      <Footer />
    </div>
  )
}

export default GioiThieu
